'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Box } from '@ironhack/design-system2/components/box';
import { Button } from '@ironhack/design-system2/components/button';
import { Card, type CardProps } from '@ironhack/design-system2/components/card';
import { FormFeedback } from '@ironhack/design-system2/components/form-feedback';
import { VStack } from '@ironhack/design-system2/components/stack';
import { Text } from '@ironhack/design-system2/components/text';
import { isEmptyDocument } from 'datocms-structured-text-utils';
import { groupBy, omit, prop } from 'ramda';
import { type ReactElement, useEffect, useState } from 'react';
import { Image } from 'react-datocms';
import { FormProvider, useForm } from 'react-hook-form';
import * as z from 'zod';

import { CheckboxField } from '@/app/components/client/checkbox-field';
import { StructuredContent } from '@/app/components/client/structured-content';
import { TextField } from '@/app/components/client/text-field';
import { useFormRequest, usePageContext } from '@/hooks';
import { type DatoBlogAd, type DatoFormInput } from '@/lib/datocms';
import { sendEvent } from '@/lib/gtm';
import { TurnstileFormToken } from '@/lib/turnstile/components';
import {
  type EmailInput,
  getEmailSchema,
} from '@/lib/utils/form-validations/email-schema';

type Props = Omit<CardProps, 'content'> & DatoBlogAd;

type FormInputs = {
  cToken: string;
  newsletter: boolean;
  legal: boolean;
} & EmailInput;

const eventBase = { eventCategory: 'blog', eventAction: 'subscribe' };
const events = {
  subscribeClick: (title: string): void =>
    sendEvent({
      ...eventBase,
      eventLabel: `post - click widget - ${title}`,
    }),
  subscribeSuccess: (title: string): void =>
    sendEvent({
      ...eventBase,
      eventLabel: `post - successfuly suscribed widget - ${title}`,
    }),
  subscribeFail: (title: string): void =>
    sendEvent({
      ...eventBase,
      eventLabel: `post - subscribed fail widget - ${title}`,
    }),
};

const getSchema = ({ emailField, legalField, submitError }: Props) =>
  z
    .object({
      cToken: z.string().min(1, submitError),
      legal: z.literal<boolean>(true, {
        errorMap: () => ({
          message: legalField.validationMessages.requiredMessage,
        }),
      }),
    })
    .merge(getEmailSchema(emailField.validationMessages))
    .required();

export const BlogAd = (props: Props): ReactElement => {
  const {
    banner,
    buttonText,
    content,
    emailField,
    inputs,
    legalField,
    salesforceSource,
    submitError,
    submitSuccess,
    title,
    ...flexProps
  } = props;

  const { Newsletter: [newsletterField] = [] } = groupBy<DatoFormInput>(
    prop('code')
  )(inputs);

  const formSchema = getSchema(props);
  const formMethods = useForm<FormInputs>({
    defaultValues: { newsletter: false, legal: false },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: zodResolver(formSchema),
  });
  const {
    formState: { errors, isDirty, isSubmitting },
    handleSubmit,
    reset,
    setValue,
    watch,
  } = formMethods;
  const [formFeedback, setFormFeedback] = useState<{
    status: 'error' | 'success';
    message: string;
  } | null>();
  const {
    params: { language },
  } = usePageContext();
  const formRequest = useFormRequest();
  const setCaptchaToken = (token: string) => setValue('cToken', token);
  const cToken = watch('cToken');

  useEffect(() => {
    if (isDirty && formFeedback?.status === 'success') {
      setFormFeedback(null);
    }
  }, [formFeedback?.status, isDirty]);

  const onCtaClick = ({ newsletter, ...data }: FormInputs): Promise<unknown> =>
    formRequest({
      data: {
        ...omit(['legal'], data),
        emailOptIn: newsletterField ? newsletter : true,
        language,
        source: salesforceSource,
      },
      formType: 'nurturing',
    });

  const onSubmitHandler = async (data: FormInputs): Promise<void> => {
    events.subscribeClick(document.title);
    setFormFeedback(null);
    try {
      await onCtaClick(data);
      events.subscribeSuccess(document.title);
      setFormFeedback({
        status: 'success',
        message: submitSuccess,
      });
      reset();
    } catch {
      events.subscribeFail(document.title);
      setFormFeedback({
        status: 'error',
        message: submitError,
      });
    }
  };

  return (
    <Card gap={2} p={2} pb={4} shadow="l" size="2xl" {...flexProps}>
      <Box borderRadius={8} overflow="hidden" w="full">
        <Image data={banner.responsiveImage} objectFit="cover" />
      </Box>
      <Text color="text.primary" textStyle="xlBold">
        {title}
      </Text>
      {!isEmptyDocument(content) && (
        <StructuredContent color="text.primary" data={content} textStyle="m" />
      )}
      <FormProvider {...formMethods}>
        <VStack
          as="form"
          noValidate
          onSubmit={handleSubmit(onSubmitHandler)}
          spacing={2}
        >
          <TextField
            error={errors.email?.message}
            isRequired
            name="email"
            placeholder={emailField.placeholder}
            type="email"
          />
          {!cToken && (
            <TurnstileFormToken
              includeMargin={false}
              language={language}
              onVerify={setCaptchaToken}
            />
          )}
          <CheckboxField
            error={errors.legal?.message}
            isRequired
            label={legalField.labelNew}
            matchCaptcha
            name="legal"
            pr={0}
          />
          {newsletterField && (
            <CheckboxField
              error={errors.newsletter?.message}
              isRequired
              label={newsletterField.labelNew}
              matchCaptcha
              name="newsletter"
              pr={0}
            />
          )}
          {/* Box to have additional top margin on button */}
          <Box width="full">
            <Button isLoading={isSubmitting} mt={1} type="submit" width="full">
              {buttonText}
            </Button>
          </Box>
          {formFeedback?.status && (
            <FormFeedback {...formFeedback} id="blogPostLeadSuccess" />
          )}
        </VStack>
      </FormProvider>
    </Card>
  );
};
